import React, { useEffect, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Container } from "@mui/material";

import BreadcrumbBuilder from "../BreadcrumbBuilder";
import ConfirmDialog from "../../../Common/Dialog/ConfirmDialog";
import FabButton from "../../../Common/Button/FabButton";
import DevelopmentNeedCard from "./DevelopmentNeedCard";
import DevelopmentNeedFormDialog from "./DevelopmentNeedFormDialog";
import Fetching from "../../../Common/Fetching";
import PropTypes from "prop-types";

import rmsActions from "../../../../actions/rmsActions";
import { getDevelopmentNeeds, getRmsAppCode, isFetching } from "../../../../reducers";
import { DataTypes } from "../../../../constants";


function DevelopmentNeedList({developmentNeeds, isFetching, onAdd, onUpdate, onDelete}) {
    const [openDialog, setOpenDialog] = useState(null);
    const [selected, setSelected] = useState(null);

    const handleOpenAddDialog = () => setOpenDialog("add");
    const handleOpenEditDialog = (developmentNeed = null) => {
        setSelected(developmentNeed);
        setOpenDialog("update");
    };
    const handleOpenDeleteDialog = (developmentNeed) => {
        setSelected(developmentNeed);
        setOpenDialog("delete");
    };
    const handleCloseDialog = useCallback(() => {
        setOpenDialog(null);
        setSelected(null);
    }, []);

    return (
        <Container className="content">
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <BreadcrumbBuilder />
                </Grid>

                {isFetching && (
                    <Grid item xs={12}>
                        <Fetching />
                    </Grid>
                )}

                {!isFetching && !!developmentNeeds && Array.isArray(developmentNeeds) &&
                    developmentNeeds
                        .sort((a, b) =>
                            (a.dateCompletion instanceof Date ? a.dateCompletion : new Date(a.dateCompletion))
                            < (b.dateCompletion instanceof Date ? b.dateCompletion : new Date(b.dateCompletion))
                                ? 1
                                : -1
                        )
                        .map((developmentNeed) => (
                            <Grid item xs={12} key={developmentNeed.id}>
                                <DevelopmentNeedCard
                                    developmentNeed={developmentNeed}
                                    onEdit={() => handleOpenEditDialog(developmentNeed)}
                                    onDelete={() => handleOpenDeleteDialog(developmentNeed)}
                                />
                            </Grid>
                        ))}
            </Grid>

            <FabButton onClick={() => handleOpenAddDialog(null)} />

            {openDialog === "delete" && !!selected && (
                <ConfirmDialog
                    open={true}
                    title="Delete activity"
                    content={`The following activity will be deleted: ${selected.title}`}
                    confirmButtonLabel="Delete"
                    onConfirm={() => onDelete(selected.id)}
                    onCancel={handleCloseDialog}
                    onClose={handleCloseDialog}
                />
            )}

            {["add", "update"].includes(openDialog) && (
                <DevelopmentNeedFormDialog
                    open={true}
                    formTitle={openDialog === "add" ? "Add development need" : "Edit development need"}
                    data={openDialog === "update" && selected}
                    onSave={openDialog === "add" ? onAdd : (params) => onUpdate(selected.id, params)}
                    onClose={handleCloseDialog}
                    submitLabel={`${openDialog === "add" ? "Add" : "Save"}`}
                    scroll="paper"
                />
            )}
        </Container>
    );
}

const DevelopmentNeedListContainer = () => {
    const dispatch = useDispatch();
    const appCode = useSelector(getRmsAppCode);
    const developmentNeeds = useSelector((state) => getDevelopmentNeeds(state));
    const isFetchingState = useSelector((state) => isFetching(state, DataTypes.DEVELOPMENT_NEEDS));

    useEffect(() => {
        if (!!appCode) {
            dispatch(rmsActions.getDevelopmentNeeds(appCode));
        }
    }, [dispatch, appCode]);

    return (
        <DevelopmentNeedList
            developmentNeeds={developmentNeeds}
            isFetching={isFetchingState}
            onAdd={(params) => dispatch(rmsActions.addDevelopmentNeed(appCode, params))}
            onUpdate={(id, params) => dispatch(rmsActions.updateDevelopmentNeed(appCode, id, params))}
            onDelete={(id) => dispatch(rmsActions.deleteDevelopmentNeed(appCode, id))}
        />
    );
};

DevelopmentNeedListContainer.propTypes = {
    getDevelopmentNeeds: PropTypes.func,
    addDevelopmentNeed: PropTypes.func,
    updateDevelopmentNeed: PropTypes.func,
    deleteDevelopmentNeed: PropTypes.func,
    developmentNeeds: PropTypes.array,
    isFetching: PropTypes.bool,
};

export default DevelopmentNeedListContainer;
